import {
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Edit, Refresh } from "@material-ui/icons";
import { Skeleton, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { useAuth } from "../../../../../../context/auth";
import { useProfileManagementList } from "../../../../../../context/lists/profileManagementList";
import { useCommittees } from "../../../../../../hooks/useCommittees";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import { updateDataBySap } from "../../../../../../services/profiles.service";
import { IMember, getAll } from "../../../../../../services/users.service";
import SplitButton from "../../../../../Common/SplitButton";
import { Table } from "../../../../../Common/Table";
import ToastMessage from "../../../../../Common/ToastMessage";
import Header from "../../../components/Header";
import {
  Container,
  CounterLabel,
  FetchMetaDataContainer,
  FilterButtonsContainer,
  FilterInputs,
  ListContainer,
  ListCounterContainer,
  ListOrderContainer,
  LoadingSkeletonContainer,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
  fullWidthContainer: {
    maxWidth: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "16px",
  },
}));

interface IBusinessUnitProps {
  actionShowSpinner: (value: boolean) => void;
}

export interface IProfileManagementListContext {
  listOptions: {
    filterParams: {
      name?: string;
      group?: string;
      role?: string;
      external?: string;
    };
    pagination: {
      page: number;
      size: number;
    };
  };
  updateFilterParams: (filterParams: {
    name?: string;
    group?: string;
    role?: string;
    external?: string;
  }) => void;
  updatePagination: (filterParams: { page: number; size: number }) => void;
}

const ListUsers: React.FC<IBusinessUnitProps> = ({ actionShowSpinner }) => {
  const effectHasAlreadyRun = React.useRef(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const { listOptions, updateFilterParams, updatePagination } =
    useProfileManagementList() as IProfileManagementListContext;
  const { userInfo } = useAuth() as any;

  const history = useHistory();
  const { getActiveCommitteesByLanguageId } = useCommittees({
    spinner: () => console.log("spinner"),
  });

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [filters, setFilters] = useState<{ name?: string; role?: string; external?: string }>(
    listOptions.filterParams
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [users, setUsers] = useState<IMember[]>([]);

  const columns = [
    {
      title: t("grid.committee.columns.name").toUpperCase(),
      field: "name",
    },
    {
      title: t("menu.managementProfiles.email").toUpperCase(),
      field: "email",
    },
    {
      title: t("menu.managementProfiles.role").toUpperCase(),
      field: "role",
    },
    {
      title: t("menu.managementProfiles.abbreviation").toUpperCase(),
      field: "abbreviation",
    },
    {
      title: t("menu.managementProfiles.board").toUpperCase(),
      field: "directoryName",
    },
    {
      title: t("menu.managementProfiles.type", "TIPO").toUpperCase(),
      field: "type",
      render: (rowData: any) =>
        rowData.external ? (
          <Chip label={t("menu.managementProfiles.external", "Externo")} size="small" color="secondary" />
        ) : (
          <Chip label={t("menu.managementProfiles.internal", "Interno")} size="small" color="primary" />
        ),
    },
  ];

  const fetchUsers = useCallback((pagination, filters) => {
    actionShowSpinner(true);
    setLoading(true);

    getAll({
      pageSize: pagination.size,
      page: pagination.page,
      ...(filters.name && { name: filters.name }),
      ...(filters.group && { groupId: filters.group.id }),
      ...(filters.role && { role: filters.role }),
      ...(filters.external !== undefined && filters.external !== "" && { external: filters.external === "true" }),
    })
      .then((data) => {
        setTotalProfiles(data.count);

        setUsers(
          data.members.map((member) => ({
            ...member,
            group:
              !member.groupName && !member.groupId
                ? t("menu.managementProfiles.noAccess")
                : member.groupName,
            role: member.position,
            directoryName: member.directoryName,
          }))
        );

        actionShowSpinner(false);
        setLoading(false);
      })
      .catch((error: any) => {
        const [errorMessage] = error.response.data.errors || [
          t(`softwareManagement.modules.users.userCreationError`),
        ];

        setToastMessage({
          open: true,
          content: errorMessage,
          type: "error",
          autoHideDuration: 4000,
        });

        actionShowSpinner(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!effectHasAlreadyRun.current) {
      getActiveCommitteesByLanguageId();
    }

    fetchUsers(
      {
        page: listOptions.pagination.page,
        size: listOptions.pagination.size,
      },
      {
        group: listOptions.filterParams.group,
        name: listOptions.filterParams.name,
        role: listOptions.filterParams.role,
      }
    );
  }, [effectHasAlreadyRun, listOptions]);

  const handleFilter = useCallback(() => {
    updateFilterParams({
      name: filters.name,
      role: filters.role,
      external: filters.external,
    });
  }, [filters]);

  const loadProfiles = useCallback(
    (pagination, filters) => {
      actionShowSpinner(true);

      getAll({
        pageSize: pagination.size,
        page: pagination.page,
        ...(filters.name && { name: filters.name }),
        ...(filters.group && { groupId: filters.group.id }),
        ...(filters.role && { role: filters.role }),
        ...(filters.external !== undefined && filters.external !== "" && { external: filters.external === "true" }),
      }).then((data) => {
        setTotalProfiles(data.count);

        setUsers(
          data.members.map((member) => ({
            ...member,
            group:
              !member.groupName && !member.groupId
                ? t("menu.managementProfiles.noAccess")
                : member.groupName,
            role: member.position,
            directoryName: member.directoryName,
          }))
        );

        actionShowSpinner(false);
      });
    },
    [listOptions]
  );

  const handleUpdateDataBySap = useCallback(
    async (id) => {
      actionShowSpinner(true);

      const response = await updateDataBySap(id);

      if (!response.error) {
        loadProfiles(listOptions.pagination, listOptions.filterParams);
      } else {
        setToastMessage({
          content: "Ocorreu um erro ao atualizar os dados do usuário",
          type: "error",
          open: true,
          autoHideDuration: 4000,
        });
      }

      actionShowSpinner(false);
    },
    [listOptions]
  );

  return (
    <Container>
      <Header
        title={t("softwareManagement.modules.users.titlePlural")}
        showVectors
        showMenuToggle
        filterInputs={
          <>
            <FilterInputs container direction="column" spacing={2}>
              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <TextField
                  name="name"
                  id="name"
                  label={t("softwareManagement.name")}
                  fullWidth
                  InputLabelProps={{
                    shrink: filters?.name?.length ?? 0 > 0 ? true : false,
                  }}
                  value={filters.name}
                  autoComplete="nope"
                  onChange={(e) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      name: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <TextField
                  name="role"
                  id="role"
                  label={t("softwareManagement.modules.users.role")}
                  fullWidth
                  InputLabelProps={{
                    shrink: filters?.role?.length ?? 0 > 0 ? true : false,
                  }}
                  value={filters.role}
                  autoComplete="nope"
                  onChange={(e) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      role: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="external-filter-label">
                    {t("menu.managementProfiles.type", "Tipo")}
                  </InputLabel>
                  <Select
                    labelId="external-filter-label"
                    id="external-filter"
                    value={filters.external || ""}
                    onChange={(e) =>
                      setFilters((oldValue) => ({
                        ...oldValue,
                        external: e.target.value as string,
                      }))
                    }
                  >
                    <MenuItem value="">{t("menu.managementProfiles.all", "Todos")}</MenuItem>
                    <MenuItem value="false">{t("menu.managementProfiles.internal", "Interno")}</MenuItem>
                    <MenuItem value="true">{t("menu.managementProfiles.external", "Externo")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </FilterInputs>

            <FilterButtonsContainer>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({
                    name: "",
                    role: "",
                    external: "",
                  });
                  updateFilterParams({
                    name: "",
                    role: "",
                  });
                }}
              >
                {t("softwareManagement.clean")}
              </Button>

              <Button
                variant="contained"
                className={classes.button}
                onClick={() => handleFilter()}
              >
                {t("softwareManagement.search")}
              </Button>
            </FilterButtonsContainer>
          </>
        }
      />

      <FetchMetaDataContainer>
        {loading ? (
          <Stack style={{ width: "100%" }} spacing={1}>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height={60}
            />
          </Stack>
        ) : (
          <>
            <ListCounterContainer>
              <CounterLabel>
                {totalProfiles}{" "}
                {t("softwareManagement.modules.users.titlePlural")}
              </CounterLabel>
            </ListCounterContainer>

            <ListOrderContainer>
              <SplitButton
                principalButton={{
                  label: t("softwareManagement.modules.users.createUser"),
                  onClick: () =>
                    history.push("/system-management/configure-users"),
                }}
                options={[
                  {
                    label: t(
                      "softwareManagement.modules.committeePackage.createCommitteePackage"
                    ),
                    onClick: () =>
                      history.push(
                        "/system-management/configure-committee-packages"
                      ),
                  },
                  {
                    label: t(
                      "softwareManagement.modules.business.createBusiness"
                    ),
                    disabled:
                      userInfo?.master !== undefined
                        ? !userInfo?.master
                        : false,
                    onClick: () =>
                      history.push("/system-management/configure-business"),
                  },
                ]}
              />
            </ListOrderContainer>
          </>
        )}
      </FetchMetaDataContainer>

      <ListContainer>
        {loading ? (
          <LoadingSkeletonContainer>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
          </LoadingSkeletonContainer>
        ) : (
          <Grid item md={12} lg={12} className={classes.fullWidthContainer}>
            <Table
              columns={columns}
              data={users}
              onRowClick={(_event: any, rowData: any) => {
                console.log(rowData);
              }}
              title="Grupos"
              actions={[
                {
                  icon: () => <Edit />,
                  tooltip: t("edit"),
                  onClick: (_event: any, rowData: any) => {
                    history.push(
                      `/system-management/configure-users?id=${rowData.id}`
                    );
                  },
                },
                {
                  icon: () => <Refresh />,
                  tooltip: t("menu.managementProfiles.updateDataBySap"),
                  onClick: (_event: any, rowData: any) => {
                    handleUpdateDataBySap(rowData.id);
                  },
                },
              ]}
              pageSize={listOptions.pagination.size}
              components={{
                Pagination: (props: any) => {
                  return (
                    <TablePagination
                      component="th"
                      count={totalProfiles}
                      style={{ float: "right" }}
                      rowsPerPageOptions={[5, 10, 20]}
                      labelRowsPerPage={t("table.labelRowsPerPage")}
                      rowsPerPage={listOptions.pagination.size}
                      page={listOptions.pagination.page}
                      labelDisplayedRows={(_data) => {
                        let numberOfPages = Math.ceil(
                          totalProfiles / listOptions.pagination.size
                        );

                        if (numberOfPages <= 1) {
                          numberOfPages = 1;
                        }

                        return `${listOptions.pagination.page + 1} ${t(
                          "table.labelDisplayedRows"
                        )} ${numberOfPages}`;
                      }}
                      onPageChange={(_e, page) => {
                        updatePagination({
                          page: page,
                          size: listOptions.pagination.size,
                        });
                      }}
                      onRowsPerPageChange={(event) => {
                        const newSize = event.target.value;

                        updatePagination({
                          page: 0,
                          size: Number(newSize),
                        });

                        props.onChangeRowsPerPage(event);
                      }}
                      nextIconButtonProps={{
                        disabled:
                          listOptions.pagination.page + 1 >=
                          Math.ceil(
                            totalProfiles / listOptions.pagination.size
                          ),
                      }}
                      backIconButtonProps={{
                        disabled: listOptions.pagination.page + 1 <= 1,
                      }}
                    />
                  );
                },
              }}
            />
          </Grid>
        )}
      </ListContainer>
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const ListUsersConnection = connect(null, mapDispatchToProps)(ListUsers);

export default ListUsersConnection;
